import AddRounded from '@mui/icons-material/AddRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { api, ApiError, unwrap } from '../../api/client';
import { VariantPicker } from '../../components/pickers';
import { money, qty as fmtQty } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import {
  emptyLine,
  lineDelta,
  stockFormSchema,
  type AdjustMode,
  type StockFormValues,
} from './forms';
import { useLocations } from './model';
import { LocationSelect } from './shared';

/** Current on-hand of one item at one location (0 when it has no balance row yet). */
function useOnHand(variantId: string | undefined, locationId: string) {
  return useQuery({
    queryKey: ['inventory', 'balances', { variantId, locationId, includeZero: true, one: true }],
    queryFn: () =>
      unwrap(
        api.GET('/stock/balances', {
          params: { query: { variantId, locationId, includeZero: 'true', pageSize: 1 } },
        }),
      ),
    enabled: Boolean(variantId && locationId),
    select: (d) => d.data[0]?.qtyOnHand ?? '0',
  });
}

function OnHandCell({
  control,
  index,
  mode,
}: {
  control: Control<StockFormValues>;
  index: number;
  mode: AdjustMode;
}) {
  const { format } = useShop();
  const [variant, locationId, direction, qty] = useWatch({
    control,
    name: [
      `lines.${index}.variant`,
      'locationId',
      `lines.${index}.direction`,
      `lines.${index}.qty`,
    ],
  });
  const onHand = useOnHand(variant?.id, locationId);
  if (!variant) return <Typography color="text.disabled">—</Typography>;
  const now = onHand.data === undefined ? undefined : Number(onHand.data);
  const after = now === undefined ? undefined : now + lineDelta(mode, { direction, qty });
  const unit = variant.baseUnit.symbol;
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', whiteSpace: 'nowrap' }}>
      <Typography variant="body2" className="tabular" color="text.secondary">
        {now === undefined ? '…' : fmtQty(now, format)}
      </Typography>
      <ArrowForwardRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
      <Typography
        variant="body2"
        className="tabular"
        sx={{
          fontWeight: 700,
          color: after !== undefined && after < 0 ? 'error.main' : 'text.primary',
        }}
      >
        {after === undefined ? '…' : fmtQty(after, format)} {unit}
      </Typography>
    </Stack>
  );
}

export interface StockEntryFormProps {
  /** Initial mode; ADJUSTMENT and WRITE_OFF can be switched by the user. */
  mode: AdjustMode;
  allowModeSwitch?: boolean;
  defaultLocationId: string;
  today: string;
  submitLabel: string;
  onSubmit: (values: StockFormValues) => Promise<unknown>;
  aside?: ReactNode;
}

export function StockEntryForm({
  mode: initialMode,
  allowModeSwitch,
  defaultLocationId,
  today,
  submitLabel,
  onSubmit,
  aside,
}: StockEntryFormProps) {
  const { format } = useShop();
  const locations = useLocations();
  const form = useForm<StockFormValues>({
    resolver: (values, ctx, options) =>
      zodResolver(stockFormSchema(values.mode))(values, ctx, options),
    defaultValues: {
      mode: initialMode,
      locationId: defaultLocationId,
      businessDate: today,
      reason: initialMode === 'OPENING' ? 'Opening stock' : '',
      lines: [emptyLine()],
    },
  });
  const { control, handleSubmit, setError, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const [mode, lines, locationId] = useWatch({ control, name: ['mode', 'lines', 'locationId'] });
  const errors: FieldErrors<StockFormValues> = formState.errors;

  const totalValue = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitCost || 0), 0);
  const unitsIn = lines.reduce((s, l) => s + Math.max(0, lineDelta(mode, l)), 0);
  const unitsOut = lines.reduce((s, l) => s + Math.max(0, -lineDelta(mode, l)), 0);
  const showCost = mode !== 'WRITE_OFF';
  const location = locations.data?.find((l) => l.id === locationId);

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (e) {
      // Map API field details (e.g. lines.0.qty) onto the form; anything else is shown by the caller.
      if (e instanceof ApiError && e.details.length) {
        for (const d of e.details) {
          const path = d.path.replace(/^body\./, '') as keyof StockFormValues;
          setError(path, { message: d.message });
        }
      }
      throw e;
    }
  });

  const grid = {
    display: 'grid',
    gap: 1.5,
    alignItems: 'start',
    gridTemplateColumns: {
      xs: '1fr 1fr',
      md:
        mode === 'ADJUSTMENT'
          ? '28px minmax(220px, 2.2fr) 150px 112px 1fr 1fr 40px'
          : mode === 'WRITE_OFF'
            ? '28px minmax(220px, 2.6fr) 150px 1fr 40px'
            : '28px minmax(220px, 2.2fr) 150px 1fr 1fr 40px',
    },
  } as const;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e).catch(() => undefined)}
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0, 1fr) 340px' },
        alignItems: 'start',
      }}
    >
      <Card>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 2.5 }}
          >
            <Box>
              <Typography variant="h5">Items</Typography>
              <Typography variant="body2" color="text.secondary">
                {mode === 'ADJUSTMENT'
                  ? 'Add stock you found (+) or remove what is missing (−).'
                  : mode === 'WRITE_OFF'
                    ? 'Quantities removed for damage, expiry or loss.'
                    : 'What you already had on the shelf, with what it cost you.'}
              </Typography>
            </Box>
            {allowModeSwitch && (
              <Controller
                control={control}
                name="mode"
                render={({ field }) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={field.value}
                    onChange={(_, v: AdjustMode | null) => v && field.onChange(v)}
                    aria-label="Adjustment kind"
                  >
                    <ToggleButton value="ADJUSTMENT" sx={{ px: 2 }}>
                      Adjustment
                    </ToggleButton>
                    <ToggleButton value="WRITE_OFF" sx={{ px: 2 }}>
                      Write-off
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            )}
          </Stack>

          <Box
            sx={{
              ...grid,
              display: { xs: 'none', md: 'grid' },
              px: 0.5,
              pb: 1,
              color: 'text.secondary',
              typography: 'caption',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <span>#</span>
            <span>Item</span>
            <span>On hand → after</span>
            {mode === 'ADJUSTMENT' && <span>Change</span>}
            <span>{mode === 'WRITE_OFF' ? 'Qty removed' : 'Quantity'}</span>
            {showCost && <span>{mode === 'OPENING' ? 'Unit cost' : 'Unit cost (optional)'}</span>}
            <span />
          </Box>

          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {fields.map((f, i) => {
              const lineErr = errors.lines?.[i];
              const line = lines[i];
              const isOut =
                mode === 'WRITE_OFF' || (mode === 'ADJUSTMENT' && line?.direction === 'out');
              return (
                <Box key={f.id} sx={{ ...grid, pt: i === 0 ? 0 : 0.5 }}>
                  <Typography
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      pt: 1.25,
                      color: 'text.disabled',
                      fontWeight: 600,
                    }}
                    className="tabular"
                  >
                    {i + 1}
                  </Typography>
                  <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                    <Controller
                      control={control}
                      name={`lines.${i}.variant`}
                      render={({ field }) => (
                        <VariantPicker
                          value={field.value}
                          onChange={(v) => {
                            field.onChange(v);
                            if (v && mode === 'OPENING' && v.purchasePrice && !line?.unitCost)
                              form.setValue(`lines.${i}.unitCost`, String(Number(v.purchasePrice)));
                          }}
                          label={`Item ${String(i + 1)}`}
                          autoFocus={i > 0 && i === fields.length - 1}
                        />
                      )}
                    />
                    {lineErr?.variant && (
                      <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
                        {lineErr.variant.message}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      pt: { md: 1.25 },
                      gridColumn: { xs: '1 / -1', md: 'auto' },
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { md: 'none' } }}
                    >
                      On hand
                    </Typography>
                    <OnHandCell control={control} index={i} mode={mode} />
                  </Box>
                  {mode === 'ADJUSTMENT' && (
                    <Controller
                      control={control}
                      name={`lines.${i}.direction`}
                      render={({ field }) => (
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          value={field.value}
                          onChange={(_, v: 'in' | 'out' | null) => v && field.onChange(v)}
                          aria-label={`Direction for line ${String(i + 1)}`}
                          sx={{ height: 40, gridColumn: { xs: '1 / -1', md: 'auto' } }}
                        >
                          <ToggleButton
                            value="in"
                            aria-label="Found"
                            sx={{ '&.Mui-selected': { color: 'success.main' } }}
                          >
                            + In
                          </ToggleButton>
                          <ToggleButton
                            value="out"
                            aria-label="Missing"
                            sx={{ '&.Mui-selected': { color: 'error.main' } }}
                          >
                            − Out
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  )}
                  <Controller
                    control={control}
                    name={`lines.${i}.qty`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Qty"
                        inputMode="decimal"
                        error={Boolean(lineErr?.qty)}
                        helperText={lineErr?.qty?.message}
                        slotProps={{
                          htmlInput: { className: 'tabular', style: { textAlign: 'right' } },
                          input: {
                            startAdornment: isOut ? (
                              <InputAdornment position="start">−</InputAdornment>
                            ) : undefined,
                            endAdornment: line?.variant ? (
                              <InputAdornment position="end">
                                {line.variant.baseUnit.symbol}
                              </InputAdornment>
                            ) : undefined,
                          },
                        }}
                      />
                    )}
                  />
                  {showCost && (
                    <Controller
                      control={control}
                      name={`lines.${i}.unitCost`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cost"
                          inputMode="decimal"
                          disabled={mode === 'ADJUSTMENT' && line?.direction === 'out'}
                          placeholder={mode === 'ADJUSTMENT' ? 'Avg cost' : undefined}
                          error={Boolean(lineErr?.unitCost)}
                          helperText={lineErr?.unitCost?.message}
                          slotProps={{
                            htmlInput: { className: 'tabular', style: { textAlign: 'right' } },
                          }}
                        />
                      )}
                    />
                  )}
                  <Tooltip title="Remove line">
                    <span>
                      <IconButton
                        aria-label={`Remove line ${String(i + 1)}`}
                        onClick={() => remove(i)}
                        disabled={fields.length === 1}
                        sx={{ mt: 0.25 }}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
          {errors.lines?.root?.message || errors.lines?.message ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {errors.lines.root?.message ?? errors.lines.message}
            </Typography>
          ) : null}
          <Button
            startIcon={<AddRounded />}
            onClick={() => append(emptyLine())}
            sx={{ mt: 2 }}
            variant="text"
          >
            Add item
          </Button>
        </CardContent>
      </Card>

      <Stack spacing={2.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Details
            </Typography>
            <Stack spacing={2}>
              <Controller
                control={control}
                name="locationId"
                render={({ field }) => (
                  <LocationSelect
                    value={field.value}
                    onChange={field.onChange}
                    error={Boolean(errors.locationId)}
                    helperText={
                      errors.locationId?.message ??
                      (location && !location.isSellable ? 'Not a sellable location' : undefined)
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name="businessDate"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Date"
                    error={Boolean(errors.businessDate)}
                    helperText={errors.businessDate?.message}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="reason"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Reason"
                    multiline
                    minRows={2}
                    required={mode !== 'OPENING'}
                    placeholder={
                      mode === 'WRITE_OFF'
                        ? 'e.g. Broken in storage'
                        : mode === 'ADJUSTMENT'
                          ? 'e.g. Found in back room'
                          : undefined
                    }
                    error={Boolean(errors.reason)}
                    helperText={errors.reason?.message}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
        <Card
          sx={(t) => {
            const p = (t.vars ?? t).palette.primary.main;
            return {
              background: `linear-gradient(160deg, color-mix(in srgb, ${p} 7%, transparent), transparent 60%)`,
            };
          }}
        >
          <CardContent>
            <Stack spacing={1.25}>
              <SummaryRow label="Items" value={String(lines.filter((l) => l.variant).length)} />
              {unitsIn > 0 && (
                <SummaryRow label="Units in" value={`+${fmtQty(unitsIn, format)}`} tone="success" />
              )}
              {unitsOut > 0 && (
                <SummaryRow label="Units out" value={`−${fmtQty(unitsOut, format)}`} tone="error" />
              )}
              {mode === 'OPENING' && (
                <>
                  <Divider />
                  <SummaryRow label="Opening value" value={money(totalValue, format)} strong />
                  <Typography variant="caption" color="text.secondary">
                    Posted as Inventory against Opening Balance Equity.
                  </Typography>
                </>
              )}
              {mode !== 'OPENING' && (
                <Typography variant="caption" color="text.secondary">
                  Posted immediately and cannot be edited. Decreases are valued at average cost.
                </Typography>
              )}
            </Stack>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              loading={formState.isSubmitting}
              sx={{ mt: 2.5 }}
            >
              {submitLabel}
            </Button>
          </CardContent>
        </Card>
        {aside}
      </Stack>
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  tone,
  strong,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'error';
  strong?: boolean;
}) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        className="tabular"
        variant={strong ? 'h5' : 'body2'}
        sx={{ fontWeight: 700, color: tone ? `${tone}.main` : undefined }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
